import Grid from '@/icons/light/grid.svg';
import List from '@/icons/light/list.svg';

import { generatedListingConfig } from '@/lib/contexts/listing-config.generated';

import type { FunctionComponent } from 'react';
import type { TypeListingViewBarProps } from './types';

/**
 * Si el tenant subió un SVG (`listingView{Grid|List}Icon`), lo
 * renderizamos vía `mask-image` + `bg-current` para que herede el color
 * del botón activo/inactivo. Sin override, SVG inline histórico.
 */
const ViewIcon = ({ type }: { type: 'grid' | 'list' }) => {
  const hasCustom =
    type === 'grid'
      ? generatedListingConfig.customViewGridIcon
      : generatedListingConfig.customViewListIcon;
  if (hasCustom) {
    const url = `/assets/images/listing-view-${type}-icon.svg`;
    return (
      <span
        aria-hidden
        className="block h-5 w-5 bg-current"
        style={{
          WebkitMaskImage: `url(${url})`,
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          WebkitMaskSize: 'contain',
          maskImage: `url(${url})`,
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
          maskSize: 'contain',
        }}
      />
    );
  }
  const Icon = type === 'grid' ? Grid : List;
  return <Icon className="h-5 w-5" />;
};

/**
 * Contador + toggle grid/list. El toggle `list` fuerza 1 columna
 * (override del `--listing-grid-cols` del tenant).
 */
export const ListingViewBar: FunctionComponent<TypeListingViewBarProps> = ({
  className = '',
  countText,
  oneCol,
  onChange,
  sortSlot,
}) => {
  // Si el tenant activó `showSort`, pintamos el control de orden a la
  // izquierda y forzamos space-between (Ordenar ←→ contador+toggle), igual
  // que el Figma de HoM. Sin `showSort` mantenemos el comportamiento
  // histórico: `end` alinea a la derecha, default = space-between.
  const showSort = generatedListingConfig.showSort;
  const justify =
    showSort || generatedListingConfig.viewBarAlign !== 'end'
      ? 'md:justify-between'
      : 'md:justify-end';

  return (
    <div
      className={`flex flex-col-reverse gap-4 md:flex-row md:items-center ${justify} ${className}`}
    >
      {/* Sin gatear por `showSort`: el slot ya se auto-oculta y así el widget
          de orden sigue montado, para que `?orden=` de la URL se aplique
          aunque el tenant no muestre el control. Mismo criterio que los
          filtros de precio/tamaño. */}
      {sortSlot}

      <div className="flex flex-row items-center gap-4">
        <p className="text-text-primary text-sm">{countText}</p>

        {/* En móvil el toggle no aplica — el grid se fuerza a 1 col por
            el `--listing-grid-cols-mobile` y el horizontal-card layout
            sólo tiene sentido con ancho de listing column desktop. md+
            recupera el toggle. */}
        <div className="hidden flex-row items-center gap-3 md:flex">
          <button
            type="button"
            aria-label="Vista en columnas"
            aria-pressed={!oneCol}
            onClick={() => onChange(false)}
            className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
              !oneCol
                ? 'text-primary-500'
                : 'text-text-neutrals-500 hover:text-text-primary'
            }`}
          >
            <ViewIcon type="grid" />
          </button>
          <button
            type="button"
            aria-label="Vista en una columna"
            aria-pressed={oneCol}
            onClick={() => onChange(true)}
            className={`flex h-7 w-7 items-center justify-center rounded transition-colors ${
              oneCol
                ? 'text-primary-500'
                : 'text-text-neutrals-500 hover:text-text-primary'
            }`}
          >
            <ViewIcon type="list" />
          </button>
        </div>
      </div>
    </div>
  );
};

ListingViewBar.displayName = 'ListingViewBar';
